import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetSkills } from "../../hooks/skills/useGetSkills";
import { useDeleteSkill } from "../../hooks/skills/useDeleteSkill";
import { useDeleteAllSkills } from "../../hooks/skills/useDeleteAllSkills";
import { useRestoreAllSkills } from "../../hooks/skills/useRestoreAllSkills";
import { useForceDeleteAllSkills } from "../../hooks/skills/useForceDeleteAllSkills";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import SkillsTableRow from "./Table/SkillsTableRow";
import { SkillsTableColumns } from "./Table/SkillsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const SkillsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("viewAny-skill"),
            create: has("create-skill"),
            edit: has("update-skill"),
            delete: has("delete-skill"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [trashedFilter, setTrashedFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    // CRUD Hooks
    const { deleteSkillMutate, isPending: isDelete } = useDeleteSkill();
    const { deleteAllSkillsMutate, isPending: isDeleteAll } = useDeleteAllSkills();
    const { restoreAllSkillsMutate, isPending: isRestoreAll } = useRestoreAllSkills();
    const { forceDeleteAllSkillsMutate, isPending: isForceDeleteAll } = useForceDeleteAllSkills();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/skills",
        deleteSingle: deleteSkillMutate,
        deleteAll: deleteAllSkillsMutate,
        restoreSingle: (id, options) => restoreAllSkillsMutate({ items: [id] }, options),
        restoreAll: restoreAllSkillsMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllSkillsMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllSkillsMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            is_active: statusFilter || undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, trashedFilter, statusFilter, order]
    );

    const { isLoading, error, skills, meta } = useGetSkills(params);

    // تجهيز الفلاتر لـ TableLayout
    const filters = useMemo(() => [
        {
            key: "is_active",
            label: { ar: "حالة المهارة", en: "Skill Status" },
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ],
        },
    ], [statusFilter]);

    const columns = useMemo(() => SkillsTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "المهارات", en: "Skills" }}
            columns={columns}
            items={skills}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, skills)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/skills/create")}
            addButtonLabel={{ ar: "إضافة مهارة", en: "Add Skill" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <SkillsTableRow
                    {...params}
                    key={params.item.id}
                    skill={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default SkillsTable;